import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

import { LoadingPanel } from './Loader';
import {
  CATEGORIES,
  loadEmoji,
  recentEmoji,
  searchEmoji,
  type Emoji,
  type EmojiSet,
} from '../../lib/emoji';

/**
 * The emoji picker.
 *
 * ## One category at a time
 *
 * There are 1,914 emoji. Rendering all of them is 1,914 buttons in the
 * document, on every composer on the page, whether or not anybody opens the
 * panel — and the largest category is 388, which a browser handles without
 * complaint.
 *
 * So the grid renders the active category only, and search replaces it with
 * matches across the whole set. That avoids virtualisation entirely, which is
 * the alternative and is a lot of machinery for a panel this size.
 *
 * ## The data arrives when the panel does
 *
 * `loadEmoji()` is a dynamic import, so the several hundred kilobytes of names
 * are fetched the first time somebody opens a picker rather than on every page
 * load. It is cached for the session, so this is a one-off flicker of a spinner
 * at most.
 *
 * ## Where it opens is measured, not declared
 *
 * `direction` and `align` are preferences now rather than instructions.
 * Composers live at the bottom of the thing they compose into, so upward is
 * usually right - but a caller cannot know how much room is under its own
 * button, and the ones that guessed "down" put a 370px panel through the
 * bottom of the window. The preference is honoured when it fits and flipped
 * when it does not.
 *
 * It is also portalled and positioned in the viewport rather than in its
 * parent. An `absolute` panel is clipped by any ancestor that scrolls or hides
 * its overflow, and these sit inside comment threads, dialogs and a chat
 * column - all three.
 */

/** The panel's own size: a search row, a tab strip, and an `h-64` grid. */
const PANEL_WIDTH = 352;
const PANEL_HEIGHT = 370;
const GAP = 8;
const EDGE = 12;

/**
 * Where a portalled panel belongs in the document.
 *
 * A modal `<dialog>` makes everything outside it inert, so a panel portalled
 * out of one is painted, in the right place, and completely unclickable -
 * which matters here because half these pickers are inside an editor dialog.
 * Native fullscreen is the same trap in reverse: only the fullscreen element
 * and its descendants are painted at all.
 */
function hostFor(node: Element | null): Element | null {
  if (typeof document === 'undefined') return null;

  return (
    node?.closest('dialog')
    ?? (document.fullscreenElement?.contains(node) ? document.fullscreenElement : null)
    ?? document.body
  );
}

/**
 * Where the panel goes, given the button and what the caller would prefer.
 *
 * Fixed rather than absolute, so nothing between here and the document can
 * clip it, and clamped to the window on both axes - a picker hanging off the
 * right edge is the same bug as one hanging off the bottom.
 */
function place(box: DOMRect, direction: 'up' | 'down', align: 'left' | 'right') {
  const width = Math.min(PANEL_WIDTH, window.innerWidth - EDGE * 2);
  const above = box.top;
  const below = window.innerHeight - box.bottom;

  // The preference, unless the other side has room and this one does not.
  const up = direction === 'up' ? above >= PANEL_HEIGHT + GAP || above >= below : below < PANEL_HEIGHT + GAP && above > below;

  const preferred = align === 'right' ? box.right - width : box.left;
  const left = Math.min(Math.max(EDGE, preferred), window.innerWidth - width - EDGE);

  return {
    position: 'fixed' as const,
    left,
    width,
    maxHeight: Math.min(PANEL_HEIGHT, (up ? above : below) - GAP - EDGE),
    ...(up ? { bottom: window.innerHeight - box.top + GAP } : { top: box.bottom + GAP }),
  };
}
export function EmojiButton({
  onSelect,
  direction = 'up',
  align = 'right',
  className = '',
  /**
   * What the trigger looks like, when the smiley is not it.
   *
   * A composer wants a button that says "emoji". A field that holds exactly one
   * wants to show the one it holds, because that is the value - not a control
   * beside it. Defaults to the smiley, so every existing caller is unchanged.
   */
  children,
  /**
   * Whether picking closes the panel.
   *
   * Off by default: people send several emoji in a row into a message, and
   * reopening the panel for each is the kind of small friction that makes a
   * feature feel grudging. On for a field that holds one, where the second
   * pick can only be a correction of the first.
   */
  closeOnSelect = false,
}: {
  onSelect: (char: string) => void;
  direction?: 'up' | 'down';
  align?: 'left' | 'right';
  className?: string;
  children?: ReactNode;
  closeOnSelect?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<ReturnType<typeof place> | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  /* Measured before paint, so it never appears in the wrong place first. */
  useLayoutEffect(() => {
    if (!open) return;

    const box = trigger.current?.getBoundingClientRect();

    if (box) setStyle(place(box, direction, align));
  }, [open, direction, align]);

  /*
   * Followed while it is open.
   *
   * A fixed panel does not move with a scrolling ancestor, and these sit in
   * comment threads and chat columns that are almost always scrolling - so
   * without this it detaches from its own button on the first wheel event.
   */
  useEffect(() => {
    if (!open) return;

    const follow = () => {
      const box = trigger.current?.getBoundingClientRect();

      if (box) setStyle(place(box, direction, align));
    };

    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);

    return () => {
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open, direction, align]);

  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;

      if (panel.current?.contains(target) || trigger.current?.contains(target)) return;

      setOpen(false);
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      setOpen(false);
      // Back to the button, so escaping the panel does not drop focus to the
      // top of the document.
      trigger.current?.focus();
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const anchor = hostFor(trigger.current);

  return (
    <div className={`relative ${className}`}>
      <button
        ref={trigger}
        type="button"
        aria-label="Add emoji"
        aria-expanded={open}
        data-emoji-trigger
        onClick={() => setOpen((current) => !current)}
        className={
          children
            ? 'flex items-center justify-center'
            : 'flex h-9 w-9 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-ink'
        }
      >
        {children ?? (
        <svg
          viewBox="0 0 20 20"
          className="h-[18px] w-[18px]"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="7.5" />
          <path d="M7 12a3.6 3.6 0 0 0 6 0" strokeLinecap="round" />
          <circle cx="7.5" cy="8" r=".9" fill="currentColor" stroke="none" />
          <circle cx="12.5" cy="8" r=".9" fill="currentColor" stroke="none" />
        </svg>
        )}
      </button>

      {open &&
        style !== null &&
        anchor !== null &&
        createPortal(
          <div
            ref={panel}
            data-emoji-panel
            style={style}
            className={[
              'z-[70] flex flex-col overflow-hidden',
              'rounded-[var(--radius-card)] border border-line bg-surface shadow-xl',
            ].join(' ')}
          >
            <Panel
              onPick={(char) => {
                onSelect(char);
                if (closeOnSelect) setOpen(false);
              }}
            />
          </div>,
          anchor,
        )}
    </div>
  );
}

function Panel({ onPick }: { onPick: (char: string) => void }) {
  const [set, setSet] = useState<EmojiSet | null>(null);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState(CATEGORIES[0]!.group);
  const [recent, setRecent] = useState<string[]>(() => recentEmoji());
  const search = useRef<HTMLInputElement>(null);
  const grid = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    void loadEmoji().then((loaded) => {
      if (!cancelled) setSet(loaded);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Focus lands in the search box, because typing is faster than scrolling for
  // anybody who knows what they are looking for.
  useEffect(() => {
    search.current?.focus();
  }, []);

  const results = useMemo(
    () => (set === null || query.trim() === '' ? null : searchEmoji(set, query)),
    [set, query],
  );
  const shown = results ?? set?.byGroup.get(group) ?? [];

  // Switching category returns to the top; without it, moving from Flags to
  // Smileys leaves you scrolled halfway down a list you have not seen.
  useEffect(() => {
    grid.current?.scrollTo({ top: 0 });
  }, [group, query]);

  const pick = (char: string) => {
    onPick(char);
    setRecent((current) => [char, ...current.filter((c) => c !== char)].slice(0, 24));
  };

  return (
    <>
      <div className="p-2">
        <input
          ref={search}
          type="search"
          aria-label="Search emoji"
          placeholder="Search emoji"
          value={query}
          data-emoji-search
          onChange={(event) => setQuery(event.target.value)}
          className="w-full rounded-[var(--radius-control)] border border-line bg-app px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-line-strong"
        />
      </div>

      {/* Hidden while searching: the tabs would filter a result set that is
          already filtered, which does nothing anybody expects. */}
      {results === null && (
        <div role="tablist" aria-label="Emoji categories" className="flex gap-0.5 border-b border-line px-2 pb-1">
          {CATEGORIES.map((category) => (
            <button
              key={category.group}
              role="tab"
              type="button"
              aria-selected={group === category.group}
              aria-label={category.label}
              title={category.label}
              data-emoji-category={category.group}
              onClick={() => setGroup(category.group)}
              className={`flex h-8 flex-1 items-center justify-center rounded-t-[var(--radius-control)] text-base transition-colors ${
                group === category.group
                  ? 'border-b-2 border-accent bg-raised'
                  : 'border-b-2 border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <span aria-hidden="true">{category.icon}</span>
            </button>
          ))}
        </div>
      )}

      <div ref={grid} className="h-64 overflow-y-auto p-2" data-emoji-grid>
        {set === null ? (
          <LoadingPanel label="Loading emoji" className="h-full" />
        ) : results !== null && results.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-soft">Nothing matches “{query}”.</p>
        ) : (
          <>
            {/* Only outside a search, and only when there is something to show. */}
            {results === null && recent.length > 0 && (
              <section className="mb-2">
                <h3 className="meta-caps px-1 pb-1">Recent</h3>
                <Grid
                  emoji={recent
                    .map((char) => set?.byChar.get(char))
                    .filter((e): e is Emoji => e !== undefined)}
                  onPick={pick}
                />
              </section>
            )}

            {results === null && (
              <h3 className="meta-caps px-1 pb-1">
                {CATEGORIES.find((c) => c.group === group)?.label ?? group}
              </h3>
            )}

            <Grid emoji={shown} onPick={pick} />
          </>
        )}
      </div>
    </>
  );
}

function Grid({ emoji, onPick }: { emoji: Emoji[]; onPick: (char: string) => void }) {
  return (
    <div className="grid grid-cols-8 gap-0.5">
      {emoji.map((item) => (
        <button
          key={item.char}
          type="button"
          // The name, not the character: a screen reader announcing the glyph
          // reads its Unicode name anyway, but a button with no accessible name
          // announces as "button".
          aria-label={item.name}
          title={item.name}
          data-emoji={item.char}
          onClick={() => onPick(item.char)}
          className="flex aspect-square items-center justify-center rounded-[var(--radius-control)] text-xl leading-none transition-colors hover:bg-raised"
        >
          <span aria-hidden="true">{item.char}</span>
        </button>
      ))}
    </div>
  );
}
